import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../i18n';

const BASE_URL = 'https://metricconverters.com';

// Helper to get path without language prefix
const getPathWithoutLang = (pathname) => {
  const langCodes = supportedLanguages.map(l => l.code).filter(c => c !== 'en');
  for (const lang of langCodes) {
    if (pathname.startsWith(`/${lang}/`) || pathname === `/${lang}`) {
      return pathname.slice(lang.length + 1) || '/';
    }
  }
  return pathname;
};

// Map of i18n language codes to og:locale format
const localeMap = {
  en: 'en_US',
  es: 'es_ES',
  de: 'de_DE',
  fr: 'fr_FR',
  pt: 'pt_BR',
  hi: 'hi_IN',
  zh: 'zh_CN'
};

const SEOHead = ({
  title,
  description,
  canonicalUrl,
  keywords,
  type = 'website',
  structuredData
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const siteName = 'MetricConverters.com';
  const fullTitle = title ? `${title} | ${siteName}` : `Free Online Unit Converter | ${siteName}`;

  // Get path without language prefix for hreflang generation
  const pathWithoutLang = getPathWithoutLang(location.pathname);

  // Auto-generate canonical URL from current path if not provided
  const canonical = canonicalUrl || `${BASE_URL}${location.pathname}`;

  // Default description if not provided
  const metaDescription = description ||
    'Free online unit converter. Convert length, weight, temperature, area, volume, time, speed, and 50+ other measurement types instantly.';

  // Default keywords
  const metaKeywords = keywords ||
    'unit converter, metric converter, length converter, weight converter, temperature converter, online calculator, measurement converter, free converter';

  // Generate hreflang URLs for all supported languages
  const generateHreflangUrl = (langCode) => {
    if (langCode === 'en') {
      return `${BASE_URL}${pathWithoutLang}`;
    }
    return `${BASE_URL}/${langCode}${pathWithoutLang}`;
  };

  return (
    <Helmet>
      {/* Set HTML lang attribute */}
      <html lang={currentLang} />

      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="MetricConverters.com" />

      {/* Canonical URL - Critical for SEO */}
      <link rel="canonical" href={canonical} />

      {/* Hreflang tags for language alternatives */}
      <link rel="alternate" hreflang="x-default" href={generateHreflangUrl('en')} />
      {supportedLanguages.map(lang => (
        <link
          key={lang.code}
          rel="alternate"
          hreflang={lang.code === 'zh' ? 'zh-CN' : lang.code}
          href={generateHreflangUrl(lang.code)}
        />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={localeMap[currentLang] || 'en_US'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />

      {/* Additional SEO Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      {/* Structured Data / JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Helper function to generate WebApplication structured data
export const generateWebAppStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "MetricConverters.com",
  "url": "https://metricconverters.com",
  "description": "Free online unit converter for length, weight, temperature, area, volume, time, speed, and 50+ other measurement types.",
  "applicationCategory": "UtilityApplication",
  "operatingSystem": "Any",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "browserRequirements": "Requires JavaScript"
});

// Helper function to generate converter page structured data
export const generateConverterStructuredData = (fromUnit, toUnit, category, conversionResult) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": `Convert ${fromUnit.plural || fromUnit.name} to ${toUnit.plural || toUnit.name}`,
  "description": `Free online ${category} converter. Convert ${fromUnit.name} to ${toUnit.name} instantly.`,
  "mainEntity": {
    "@type": "SoftwareApplication",
    "name": `${fromUnit.name} to ${toUnit.name} Converter`,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://metricconverters.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `${category.charAt(0).toUpperCase() + category.slice(1)} Converter`,
        "item": `https://metricconverters.com/convert/${category}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `${fromUnit.name} to ${toUnit.name}`,
        "item": `https://metricconverters.com/convert/${category}/${fromUnit.id}-to-${toUnit.id}`
      }
    ]
  }
});

// Helper function to generate FAQ structured data for conversion pages
export const generateFAQStructuredData = (fromUnit, toUnit, formula, sampleConversions) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": `How do I convert ${fromUnit.plural || fromUnit.name} to ${toUnit.plural || toUnit.name}?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": formula || `To convert ${fromUnit.name} to ${toUnit.name}, use our free online converter above. Enter the value and get instant results.`
      }
    },
    {
      "@type": "Question",
      "name": `What is 1 ${fromUnit.name} in ${toUnit.plural || toUnit.name}?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": sampleConversions?.[0] || `Use our converter above to find out how many ${toUnit.plural || toUnit.name} are in 1 ${fromUnit.name}.`
      }
    }
  ]
});

// Helper function to generate category index page structured data
export const generateCategoryStructuredData = (category, description, units) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": `${category.charAt(0).toUpperCase() + category.slice(1)} Unit Converter`,
  "description": description,
  "url": `https://metricconverters.com/convert/${category}`,
  "mainEntity": {
    "@type": "ItemList",
    "numberOfItems": units?.length || 0,
    "itemListElement": units?.slice(0, 10).map((unit, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": unit.name,
      "url": `https://metricconverters.com/convert/${category}`
    })) || []
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://metricconverters.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": `${category.charAt(0).toUpperCase() + category.slice(1)} Converter`,
        "item": `https://metricconverters.com/convert/${category}`
      }
    ]
  }
});

export default SEOHead;
